import type { vtkImageData } from '@kitware/vtk.js/Common/DataModel/ImageData';

import { hull } from '../../utils/hull';

export class ThresholdSegmentation {
  private image: vtkImageData;
  private width: number;
  private height: number;
  private seedPoint: [number, number];
  private seedPointSignalIntensity: number;

  private thresholdUpper: number;
  private thresholdLower: number;
  private CONNECTIVITY = 4;
  private MIN_HOLE_REGION_LENGTH = 3;
  private THRESHOLD_SENSITIVITY = 5;

  constructor(image: vtkImageData, seedPoint: [number, number]) {
    if (!image) {
      throw new Error('Invalid image: Image data is required');
    }

    this.image = image;
    this.width = Math.round(image.getDimensions()[0]);
    this.height = Math.round(image.getDimensions()[1]);

    if (this.width <= 0 || this.height <= 0) {
      throw new Error('Invalid image dimensions');
    }

    if (!seedPoint || seedPoint.length !== 2) {
      throw new Error('Invalid seed point: [x, y] coordinates required');
    }

    // Ensure seed point is within image bounds
    const [x, y] = [Math.round(seedPoint[0]), Math.round(seedPoint[1])];
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      throw new Error(
        `Seed point [${x}, ${y}] is outside image bounds [${this.width}, ${this.height}]`,
      );
    }

    this.seedPoint = [x, y];
    this.seedPointSignalIntensity = Math.round(this._computeSeedMedian());

    this.thresholdUpper = this.seedPointSignalIntensity;
    this.thresholdLower = this.seedPointSignalIntensity;
  }

  private _getPixelSignalIntensity(x: number, y: number): number {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return 0; // Return default value for out-of-bounds
    }

    const idx = y * this.width + x;
    const scalars = this.image.getPointData().getScalars();
    if (!scalars || !scalars.getData()) {
      return 0;
    }

    return scalars.getData()[idx];
  }

  // Compute mean of intensities in the 8‐neighborhood of the seed
  private _computeSeedMean(): number {
    // get the list of coords, then map to intensities
    const coords = this._get8NeighborCoords(
      this.seedPoint[0],
      this.seedPoint[1],
    );
    const values = coords.map(([nx, ny]) =>
      this._getPixelSignalIntensity(nx, ny),
    );
    const sum = values.reduce((s, v) => s + v, 0);
    return values.length > 0 ? sum / values.length : 0;
  }

  private _computeSeedMedian(): number {
    const coords = this._get8NeighborCoords(
      this.seedPoint[0],
      this.seedPoint[1],
    );
    if (coords.length === 0) {
      return this._getPixelSignalIntensity(
        this.seedPoint[0],
        this.seedPoint[1],
      );
    }

    const values = coords.map(([nx, ny]) =>
      this._getPixelSignalIntensity(nx, ny),
    );
    if (values.length === 0) {
      return 0;
    }

    return values.sort((a, b) => a - b)[Math.floor(values.length / 2)];
  }

  // 8 connectivity around (x,y)
  private _get8NeighborCoords(x: number, y: number): [number, number][] {
    const out: [number, number][] = [];
    // All 8 directions (including diagonals)
    const directions = [
      [-1, -1],
      [0, -1],
      [1, -1],
      [-1, 0],
      [1, 0],
      [-1, 1],
      [0, 1],
      [1, 1],
    ];
    directions.forEach(([dx, dy]) => {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height) {
        out.push([nx, ny]);
      }
    });
    return out;
  }

  // 4 connectivity around (x,y)
  private _get4NeighborCoords(x: number, y: number): [number, number][] {
    const out: [number, number][] = [];
    // Only cardinal directions (no diagonals)
    const directions = [
      [0, -1],
      [1, 0],
      [0, 1],
      [-1, 0],
    ];
    directions.forEach(([dx, dy]) => {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height) {
        out.push([nx, ny]);
      }
    });
    return out;
  }

  public setThresholdsChange(upper: number, lower: number): void {
    this.thresholdUpper
      = this.seedPointSignalIntensity
        + this.seedPointSignalIntensity * upper * this.THRESHOLD_SENSITIVITY;
    this.thresholdLower
      = this.seedPointSignalIntensity
        - this.seedPointSignalIntensity * lower * this.THRESHOLD_SENSITIVITY;
  }

  public getContours(): number[][][] {
    const region = this._regionGrow();

    // Ensure we have enough points for hull computation
    if (!region || region.length < 3) {
      return [[[]]]; // Return empty contour
    }
    const contour = hull(region, 5);
    const innerRegions = this.getInnerRegions(region);

    // Validate and filter inner contours
    const innerContours = innerRegions
      .map((innerRegion) => {
        // Need at least 3 points for hull algorithm
        if (innerRegion.length < 3) return null;

        // Validate points in inner region
        const validRegion = innerRegion.filter(
          point =>
            Array.isArray(point)
            && point.length === 2
            && typeof point[0] === 'number'
            && typeof point[1] === 'number'
            && !Number.isNaN(point[0])
            && !Number.isNaN(point[1]),
        );

        if (validRegion.length < 3) return null;

        try {
          const hullResult = hull(validRegion, 5) as number[][];

          // Validate hull result
          return hullResult.length >= 3
            && hullResult.every(
              point =>
                Array.isArray(point)
                && point.length === 2
                && !Number.isNaN(point[0])
                && !Number.isNaN(point[1]),
            )
            ? hullResult
            : null;
        }
        catch (error) {
          console.error('Hull computation error:', error);
          return null;
        }
      })
      .filter(c => c !== null);

    return [contour, ...innerContours];
  }

  private _regionGrow(): [number, number][] {
    const [sx, sy] = this.seedPoint;
    const visited = new Uint8Array(this.width * this.height);
    const region: [number, number][] = [];
    const stack: [number, number][] = [[sx, sy]];

    // 4 or 8 connectivity
    const getNeighbors
      = this.CONNECTIVITY === 4
        ? this._get4NeighborCoords.bind(this)
        : this._get8NeighborCoords.bind(this);

    while (stack.length) {
      const [x, y] = stack.pop()!;
      const idx = y * this.width + x;
      if (visited[idx]) continue;
      visited[idx] = 1;

      const val = this._getPixelSignalIntensity(x, y);
      if (val < this.thresholdLower || val > this.thresholdUpper) continue;

      region.push([x, y]);

      getNeighbors.call(this, x, y).forEach(([nx, ny]) => {
        const nIdx = ny * this.width + nx;
        if (!visited[nIdx]) {
          stack.push([nx, ny]);
        }
      });
    }

    return region;
  }

  public getInnerRegions(region: [number, number][]): [number, number][][] {
    // Skip processing if region is too small
    if (region.length < 10) {
      return [];
    }

    // ToDo:
    // - Use a more efficient algorithm for finding inner regions

    // Create a bounding box to optimize the process
    let minX = this.width;
    let maxX = 0;
    let minY = this.height;
    let maxY = 0;
    region.forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });

    // Add padding
    minX = Math.max(0, minX - 1);
    minY = Math.max(0, minY - 1);
    maxX = Math.min(this.width - 1, maxX + 1);
    maxY = Math.min(this.height - 1, maxY + 1);

    const width = maxX - minX + 1;
    const height = maxY - minY + 1;

    // ToDo:
    // limit size
    if (width > 100 || height > 100) {
      return [];
    }

    // Create a small grid for the region
    const grid = new Uint8Array(width * height);

    // Mark region pixels
    region.forEach(([x, y]) => {
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
        const localX = x - minX;
        const localY = y - minY;
        grid[localY * width + localX] = 1;
      }
    });

    // Mark border as visited (2)
    // This helps us separate exterior from hole regions
    const visited = new Uint8Array(width * height);
    const queue: [number, number][] = [];

    // Add border pixels to the queue
    for (let x = 0; x < width; x++) {
      queue.push([x, 0]);
      queue.push([x, height - 1]);
      visited[0 * width + x] = 2;
      visited[(height - 1) * width + x] = 2;
    }

    for (let y = 1; y < height - 1; y++) {
      queue.push([0, y]);
      queue.push([width - 1, y]);
      visited[y * width + 0] = 2;
      visited[y * width + (width - 1)] = 2;
    }

    // Process the queue to mark all exterior regions
    while (queue.length > 0) {
      const [x, y] = queue.shift()!;
      const idx = y * width + x;

      // We only expand through non-region pixels
      if (grid[idx] === 1) continue;

      // Check 4-connected neighbors
      const neighbors = [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ];

      neighbors.forEach(([nx, ny]) => {
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) return;

        const nIdx = ny * width + nx;
        if (visited[nIdx] === 0 && grid[nIdx] === 0) {
          visited[nIdx] = 2;
          queue.push([nx, ny]);
        }
      });
    }

    // Now find hole regions - any unvisited, non-region pixels
    const holeRegions: [number, number][][] = [];

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;

        // If pixel is unvisited (not exterior) and not part of region, it's a hole
        if (visited[idx] === 0 && grid[idx] === 0) {
          // Found a new hole region - flood fill to collect all connected pixels
          const holeRegion: [number, number][] = [];
          const holeQueue: [number, number][] = [[x, y]];

          while (holeQueue.length > 0) {
            const [hx, hy] = holeQueue.shift()!;
            const hIdx = hy * width + hx;

            // Skip if already processed
            if (visited[hIdx] !== 0 || grid[hIdx] !== 0) continue;

            // Mark as processed (hole)
            visited[hIdx] = 1;

            // Add to hole region (convert back to image coordinates)
            holeRegion.push([hx + minX, hy + minY]);

            // Check neighbors
            const neighbors = [
              [hx + 1, hy],
              [hx - 1, hy],
              [hx, hy + 1],
              [hx, hy - 1],
            ];

            neighbors.forEach(([nx, ny]) => {
              if (nx < 0 || ny < 0 || nx >= width || ny >= height) return;

              const nIdx = ny * width + nx;
              if (visited[nIdx] === 0 && grid[nIdx] === 0) {
                holeQueue.push([nx, ny]);
              }
            });
          }

          // Add hole region if it's large enough
          if (holeRegion.length > this.MIN_HOLE_REGION_LENGTH) {
            holeRegions.push(holeRegion);
          }
        }
      }
    }
    return holeRegions;
  }
}
